/**
 * Outils de la page "Eau à 3 mois" (port de l'app Shiny "Besoin en eau en cours
 * de campagne", 05_helpers_graphiques.R) : correspondance sélecteur ->
 * scénario, dates, statistiques, estimation de densité.
 */

export const GRANDEURS_CULTURE = [
  { id: 'culture', label: 'Besoin en eau de la culture' },
  { id: 'irrigation', label: 'Besoin en irrigation' },
];

// Part du besoin de la plante retenue comme besoin d'irrigation (hypothèse de
// travail du Shiny, PART_IRRIGATION). Appliquée à la lecture, comme là-bas :
// une proportionnalité commute avec la somme et les quantiles.
export const PART_IRRIGATION = 0.05;

export const FENETRE_FIN = '2026-10-15'; // borne haute de la distribution

export function coefficientGrandeur(type, grandeur) {
  return type === 'culture' && grandeur === 'irrigation' ? PART_IRRIGATION : 1;
}

export function libelleGrandeur(type, grandeur) {
  if (type === 'elevage') return 'Abreuvement';
  return GRANDEURS_CULTURE.find((g) => g.id === grandeur)?.label ?? 'Besoin en eau de la culture';
}

/**
 * Production choisie dans le SelecteurProduction -> scénario du Shiny.
 * Le jeu de démonstration ne couvre que : Maïs Précoce / Tardif (semis 04-15 et
 * 05-05), Vache laitière (5 rations), Porc à l'engraissement (Soupe/Sec x
 * bol/pipette). Les variétés et dates intermédiaires sont ramenées au plus
 * proche : à remplacer par la vraie route.
 */
const RATIONS_VACHE = {
  1: 'Ensilage mais', 2: 'Foin et paille', 3: 'Paturage',
  4: 'Ensilage mais et paturage', 5: 'Foin et paturage',
};
const SEMIS_DISPONIBLES = ['04-15', '05-05'];

function jourDeLAnnee(mmdd) {
  const [m, j] = mmdd.split(/[.-]/).map(Number);
  return Math.round((Date.UTC(2026, m - 1, j) - Date.UTC(2026, 0, 1)) / 864e5);
}

export function scenarioDepuisProduction({ type, idProduction, semis, alimentation, abreuvement }) {
  if (type === 'culture') {
    const systeme = [1187, 1188].includes(Number(idProduction)) ? 'Maïs Tardif' : 'Maïs Précoce';
    const cible = jourDeLAnnee(semis ?? '04.15');
    const modalite = SEMIS_DISPONIBLES.reduce((meilleur, m) =>
      Math.abs(jourDeLAnnee(m) - cible) < Math.abs(jourDeLAnnee(meilleur) - cible) ? m : meilleur
    );
    return { systeme, modalite, famille: 'Grandes cultures', origine: semis ?? modalite };
  }
  if (String(idProduction).startsWith('C')) {
    const modalite = `${Number(alimentation) === 7 ? 'Sec' : 'Soupe'}-${Number(abreuvement) === 2 ? 'Pipette' : 'Bol'}`;
    return { systeme: "Porc à l'engraissement", modalite, famille: 'Élevage', origine: '04.15' };
  }
  return {
    systeme: 'Vache laitière',
    modalite: RATIONS_VACHE[Number(alimentation)] ?? RATIONS_VACHE[1],
    famille: 'Élevage',
    origine: '04.15',
  };
}

// ── dates ───────────────────────────────────────────────────────────────────

export function ajouterJours(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Timestamp renvoyé par ECharts (axe temporel) -> AAAA-MM-JJ. ECharts lit une
 * chaîne "AAAA-MM-JJ" comme minuit HEURE LOCALE : repasser par toISOString()
 * (UTC) décalerait la date d'un jour en France l'été.
 */
export function isoDepuisTs(ts) {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** AAAA-MM-JJ -> "1 octobre" */
export function formatJourMois(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** AAAA-MM-JJ -> JJ/MM */
export function formatJJMM(iso) {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

export function formatCourt(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}
export function formatLong(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}
export function formatJJMMAAAA(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { timeZone: 'UTC' });
}

// ── statistiques ────────────────────────────────────────────────────────────

/** Quantile de type 7 (celui de R par défaut) sur un tableau TRIÉ. */
export function quantile(trie, p) {
  const n = trie.length;
  if (n === 1) return trie[0];
  const h = (n - 1) * p;
  const lo = Math.floor(h);
  return trie[lo] + (h - lo) * ((trie[Math.min(lo + 1, n - 1)] ?? trie[lo]) - trie[lo]);
}

export function resume(valeurs) {
  const t = [...valeurs].sort((a, b) => a - b);
  const moy = t.reduce((s, v) => s + v, 0) / t.length;
  return {
    n: t.length, min: t[0], q25: quantile(t, 0.25), med: quantile(t, 0.5), moy,
    q75: quantile(t, 0.75), max: t[t.length - 1],
    sd: t.length > 1 ? Math.sqrt(t.reduce((s, v) => s + (v - moy) ** 2, 0) / (t.length - 1)) : 0,
  };
}

/**
 * Toutes les trajectoires au même point ? (le jour de la coupure : avant elle,
 * les trajectoires sont identiques). On teste avec une tolérance, pas sd === 0 :
 * des valeurs identiques sommées en flottant donnent un écart-type de l'ordre
 * de 1e-13, qui ferait dessiner un pic gigantesque.
 */
export function estDegenere(r) {
  return r.max - r.min <= 1e-9 * Math.max(1, Math.abs(r.moy));
}

/** Bande passante de Silverman (bw.nrd0 de R). */
export function bwNrd0(valeurs) {
  const r = resume(valeurs);
  const hi = r.sd;
  let lo = Math.min(hi, (r.q75 - r.q25) / 1.34);
  if (!lo) lo = hi || Math.abs(r.min) || 1;
  return 0.9 * lo * Math.pow(valeurs.length, -0.2);
}

/** Densité gaussienne évaluée sur une grille régulière de n points. */
export function densite(valeurs, bw, de, a, n = 256) {
  const xs = new Array(n);
  const ys = new Array(n);
  const pas = (a - de) / (n - 1);
  const c = 1 / (valeurs.length * bw * Math.sqrt(2 * Math.PI));
  for (let i = 0; i < n; i++) {
    const x = de + i * pas;
    let s = 0;
    for (let k = 0; k < valeurs.length; k++) {
      const u = (x - valeurs[k]) / bw;
      if (u > -6 && u < 6) s += Math.exp(-0.5 * u * u);
    }
    xs[i] = x;
    ys[i] = c * s;
  }
  return { xs, ys };
}

/**
 * Bornes COMMUNES à toutes les dates de la fenêtre (bornes_distribution du
 * Shiny) : bande passante médiane, étendue absolue élargie de 3 bandes
 * passantes, densité maximale rencontrée. Calculé UNE fois par scénario pour
 * que le cadre ne bouge pas quand on déplace la date.
 * @param matrice jours x trajectoires (déjà dans l'unité d'affichage)
 */
export function bornesDistribution(matrice, { marge = 0.02, cut = 3, n = 256 } = {}) {
  const bws = matrice
    .map((v) => (estDegenere(resume(v)) ? null : bwNrd0(v)))
    .filter((b) => b && Number.isFinite(b) && b > 0)
    .sort((a, b) => a - b);
  let min = Infinity;
  let max = -Infinity;
  matrice.forEach((v) => v.forEach((x) => { if (x < min) min = x; if (x > max) max = x; }));
  const bw = bws.length ? quantile(bws, 0.5) : Math.max((max - min) / 50, Number.EPSILON);
  const pad = (max - min) * marge + cut * bw;
  const xlim = [min - pad, max + pad];
  let ymax = 0;
  matrice.forEach((v) => {
    if (!estDegenere(resume(v))) ymax = Math.max(ymax, Math.max(...densite(v, bw, xlim[0], xlim[1], n).ys));
  });
  return { xlim, ylim: [0, ymax * 1.08], bw, n };
}

export function formatNombre(v, decimales = 1) {
  return Number(v).toLocaleString('fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}
